import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import {
  listGovernanceGates,
  listGovernanceChecks,
  listWebsiteSnapshots,
} from "@/lib/governance.functions";
import { PortalShell } from "@/components/portal/PortalShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute("/_authenticated/app/governance")({
  component: GovernancePage,
});

function GovernancePage() {
  const gatesFn = useServerFn(listGovernanceGates);
  const checksFn = useServerFn(listGovernanceChecks);
  const snapsFn = useServerFn(listWebsiteSnapshots);

  const gates = useQuery({ queryKey: ["gov", "gates"], queryFn: () => gatesFn() });
  const checks = useQuery({
    queryKey: ["gov", "checks"],
    queryFn: () => checksFn({ data: { limit: 100 } }),
  });
  const snaps = useQuery({ queryKey: ["gov", "snaps"], queryFn: () => snapsFn() });

  const NAV = [
    { to: "/app/portal", label: "Übersicht" },
    { to: "/app/governance", label: "Governance" },
    { to: "/app/admin", label: "Admin" },
  ];
  return (
    <PortalShell nav={NAV}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Governance & Security</h1>
            <p className="text-sm text-muted-foreground">
              Truth → Meta → Red Team → Quality · aktuell Shadow-Mode
            </p>
          </div>
          <Badge variant="outline">Shadow-Mode aktiv</Badge>
        </div>

        <Tabs defaultValue="gates">
          <TabsList>
            <TabsTrigger value="gates">Gates</TabsTrigger>
            <TabsTrigger value="checks">Checks</TabsTrigger>
            <TabsTrigger value="snapshots">Website-Snapshots</TabsTrigger>
          </TabsList>

          <TabsContent value="gates" className="mt-4">
            <Card>
              <CardHeader><CardTitle>Konfigurierte Gates</CardTitle></CardHeader>
              <CardContent>
                <div className="grid gap-2">
                  {gates.data?.gates.map((g: any) => (
                    <div key={g.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                      <div>
                        <div className="font-medium">{g.entity_type}</div>
                        <div className="text-xs text-muted-foreground">
                          Stages: {g.required_stages.join(" → ")} · min. Score {g.min_score}
                        </div>
                      </div>
                      <Badge variant={g.blocking ? "destructive" : "secondary"}>
                        {g.blocking ? "blocking" : "shadow"}
                      </Badge>
                    </div>
                  ))}
                  {!gates.data?.gates.length && (
                    <p className="text-sm text-muted-foreground">Keine Gates konfiguriert.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="checks" className="mt-4">
            <Card>
              <CardHeader><CardTitle>Letzte Checks</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {checks.data?.checks.map((c: any) => (
                    <div key={c.id} className="border rounded-md px-3 py-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{c.entity_type} · {c.stage}</span>
                        <Badge variant={c.status === "passed" ? "secondary" : "destructive"}>
                          {c.status} {c.score != null ? `· ${c.score}` : ""}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        rule {c.rule_version} · prompt {c.prompt_version} · model {c.model_version}
                        {c.total_tokens != null && ` · ${c.total_tokens} tok`}
                      </div>
                    </div>
                  ))}
                  {!checks.data?.checks.length && (
                    <p className="text-sm text-muted-foreground">Noch keine Checks.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="snapshots" className="mt-4">
            <Card>
              <CardHeader><CardTitle>Geschützte Website-Routen</CardTitle></CardHeader>
              <CardContent>
                <div className="text-sm space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {snaps.data?.routes.map((r) => (
                      <Badge key={r} variant="outline">{r}</Badge>
                    ))}
                  </div>
                  <div className="mt-4 space-y-1">
                    {snaps.data?.snapshots.map((s: any) => (
                      <div key={s.id} className="flex items-center justify-between border rounded-md px-3 py-2">
                        <span>{s.route_path}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(s.captured_at).toLocaleString()} · {s.content_hash.slice(0, 10)}…
                          {s.is_baseline && " · baseline"}
                        </span>
                      </div>
                    ))}
                    {!snaps.data?.snapshots.length && (
                      <p className="text-sm text-muted-foreground">Noch keine Snapshots aufgenommen.</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </PortalShell>
  );
}
